import { Pagination, SupplyProfile } from '@ekkogmbh/apisdk';
import Permissions from 'src/Common/Permissions';
import { useCallback, useState } from 'react';
import { ColumnDef, DataTable, dataTableHighlight } from 'src/Common/Components/DataTable';
import { GenericDialog } from 'src/Common/Components/GenericDialog';
import { SupplyProfilePanel } from './SupplyProfilePanel';
import { Add, Delete, Edit } from '@mui/icons-material';
import { ContentControl } from 'src/Common/Components/ContentControl';
import { ContentControlButton } from 'src/Common/Components/ContentControl/ContentControlButton';
import ReceivingAreaMappingChip from './ReceivingAreaMappingChip';
import ScrollableStack from 'src/Common/Components/ScrollableStack';
import useEkko from 'src/Common/Providers/EkkoProvider';
import useRequest from 'src/Common/Hooks/useRequest';
import { useContentControl } from 'src/Common/Stores/ContentControlProvider';
import useNavigation from 'src/Common/Providers/NavigationProvider';
import usePagination from 'src/Common/Providers/PaginationProvider';

// Matches two-line chip content height: chip height (48) + chip margin (8 top + 8 bottom) = 64px,
// so scroll indicators align at the same vertical position across all scrollable columns.
const ROW_MIN_HEIGHT = 64;

export const SupplyProfileManagementContent = (): React.JSX.Element => {
  const { api } = useEkko();
  const { setPanelOpen } = useContentControl();
  const { refresh } = usePagination();
  const { scrollTop } = useNavigation();
  const [, startLoading] = useRequest();

  const [isDialogOpen, setDialogOpen] = useState<boolean>(false);
  const [selectedProfile, setSelectedProfile] = useState<SupplyProfile>();

  const dismiss = useCallback(() => {
    setPanelOpen(false);
    setDialogOpen(false);
    setSelectedProfile(undefined);
  }, []);

  const fetch = useCallback((pagination: Pagination) => api.getSupplyProfiles(pagination), [api]);

  const deleteProfile = useCallback(() => {
    if (selectedProfile === undefined) {
      return;
    }
    startLoading(
      () => api.deleteSupplyProfile(selectedProfile),
      () => {
        dismiss();
        refresh();
      },
    );
  }, [api, selectedProfile]);

  const columns: ColumnDef[] = [
    {
      header: 'Name',
      body: (entry: SupplyProfile, searchText?: string) => (
        <ScrollableStack direction={'row'} width={'100%'} minHeight={ROW_MIN_HEIGHT} alignItems={'center'}>
          {searchText ? dataTableHighlight(entry.name ?? '', [searchText]) : entry.name}
        </ScrollableStack>
      ),
      filterField: 'name',
      sortField: 'SP.name',
      proportionalContent: '15%',
    },
    {
      header: 'Coordinate',
      body: (entry: SupplyProfile, searchText?: string) => (
        <ScrollableStack direction={'row'} width={'100%'} minHeight={ROW_MIN_HEIGHT} alignItems={'center'}>
          {searchText ? dataTableHighlight(entry.coordinate ?? '', [searchText]) : entry.coordinate}
        </ScrollableStack>
      ),
      filterField: 'coordinate',
      sortField: 'N.materializedPath',
      proportionalContent: '15%',
    },
    {
      header: 'Receiving-Areas',
      body: (entry: SupplyProfile) => (
        <ScrollableStack direction={'row'} width={'100%'} minHeight={ROW_MIN_HEIGHT} spacing={1}>
          {entry.receivingAreaMappings.map((mapping, index) => (
            <ReceivingAreaMappingChip key={index} mapping={mapping} />
          ))}
        </ScrollableStack>
      ),
      proportionalContent: '70%',
    },
  ];

  return (
    <ContentControl
      panel={<SupplyProfilePanel supplyProfile={selectedProfile} closeHandler={dismiss} />}
      dialog={
        <GenericDialog
          title={'Delete Supply Profile'}
          text={selectedProfile?.name ?? ''}
          type={'confirmation'}
          maxWidth={'sm'}
          onClose={dismiss}
          onConfirm={deleteProfile}
          fullWidth
          centered
          open={isDialogOpen}
        />
      }
      buttons={[
        <ContentControlButton
          key={'add'}
          content={<Add />}
          tooltip={'Add Supply Profile'}
          onClick={() => {
            dismiss();
            setPanelOpen(true);
          }}
          requiredPermission={Permissions.EKANBAN_WRITE}
        />,
      ]}
    >
      <DataTable
        actions={[
          {
            title: 'Edit',
            icon: <Edit />,
            onClick: (entry: SupplyProfile) => {
              dismiss();
              setSelectedProfile(entry);
              setPanelOpen(true);
              scrollTop();
            },
          },
          {
            title: 'Delete',
            icon: <Delete />,
            onClick: (entry: SupplyProfile) => {
              dismiss();
              setSelectedProfile(entry);
              setDialogOpen(true);
            },
          },
        ]}
        columns={columns}
        fetchItems={fetch}
      />
    </ContentControl>
  );
};
